/**
 * Invoice Details Dialog Component
 * Dialog for viewing invoice details with line items
 */

import React from 'react';
import { Receipt, FileSpreadsheet, Mail, CreditCard } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { PaymentHistoryTimeline } from './PaymentHistoryTimeline';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const configs = {
    paid: {
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-600',
      border: 'border-emerald-500/20',
      dot: 'bg-emerald-500',
      label: 'Paid'
    },
    partially_paid: {
      bg: 'bg-amber-500/10',
      text: 'text-amber-700',
      border: 'border-amber-500/30',
      dot: 'bg-amber-500',
      label: 'Partially Paid'
    },
    invoiced: {
      bg: 'bg-blue-500/10',
      text: 'text-blue-600',
      border: 'border-blue-500/20',
      dot: 'bg-blue-500',
      label: 'Invoiced'
    },
    unbilled: {
      bg: 'bg-amber-500/10',
      text: 'text-amber-600',
      border: 'border-amber-500/20',
      dot: 'bg-amber-500',
      label: 'Unbilled'
    }
  };
  
  const config = configs[status] || configs.unbilled;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

export const InvoiceDetailsDialog = ({
  open,
  onOpenChange,
  invoice,
  formatCurrency,
  formatDate,
  onDownloadXLSX,
  onEmailInvoice,
  onMarkPaid
}) => {
  if (!invoice) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-500" />
            Invoice {invoice.invoice_number}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-sm">
            <div>
              <span className="text-slate-500">Client:</span>
              <span className="ml-2 font-medium text-slate-800">{invoice.client_code} — {invoice.client_name}</span>
            </div>
            <div>
              <span className="text-slate-500">Status:</span>
              <span className="ml-2"><StatusBadge status={invoice.status} /></span>
            </div>
            <div>
              <span className="text-slate-500">Period:</span>
              <span className="ml-2 text-slate-700">{formatDate(invoice.date_from)} — {formatDate(invoice.date_to)}</span>
            </div>
            <div>
              <span className="text-slate-500">Created:</span>
              <span className="ml-2 text-slate-700">{formatDate(invoice.created_at)}</span>
            </div>
          </div>
          
          <div className="border rounded-lg overflow-hidden max-h-[280px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="tf-thead sticky top-0">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Product ID</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Property</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items?.map((item, idx) => {
                  // May 2, 2026 — Per-parcel billing.
                  // Show "× N parcels" inline when qty > 1 so accounting
                  // and customers can audit the multiplier on each line.
                  const qty = Math.max(parseInt(item.parcel_count || item.quantity || 1, 10), 1);
                  const unitRate = item.base_price ?? item.unit_price ?? 0;
                  return (
                    <tr key={idx} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      {/* INV-PRODUCT-ID-001: snapshot product_id (legacy lines without it fall back to order #) */}
                      <td className="px-4 py-2.5 font-mono text-slate-700" title={item.order_number ? `Order ${item.order_number}` : undefined} data-testid={`invoice-line-product-id-${idx}`}>{item.product_id || item.order_number}</td>
                      <td className="px-4 py-2.5 text-slate-600">
                        <div className="flex flex-col gap-0.5">
                          <span>{item.product}</span>
                          {qty > 1 && (
                            <span
                              className="inline-flex items-center self-start gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200"
                              data-testid={`invoice-line-parcels-${idx}`}
                            >
                              {qty} Parcels
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 truncate max-w-[180px]">{item.property_address}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-800 tabular-nums">
                        <div className="flex flex-col items-end">
                          <span>{formatCurrency(item.unit_price)}</span>
                          {qty > 1 && (
                            <span className="text-[10px] text-slate-400 font-normal">
                              {formatCurrency(unitRate)} × {qty}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-100">
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-right font-semibold text-slate-700">Total:</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-600 tabular-nums">{formatCurrency(invoice.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          {invoice.notes && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <span className="text-amber-700 text-sm font-medium">Notes:</span>
              <p className="mt-1 text-sm text-amber-800">{invoice.notes}</p>
            </div>
          )}
          
          {/* Payment History Timeline (Apr 26, 2026) */}
          <div className="pt-2 border-t border-slate-100">
            <PaymentHistoryTimeline invoiceId={invoice.id} refreshKey={open ? invoice.id : null} />
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 w-full sm:w-auto">
            {onDownloadXLSX && (
              <Button variant="outline" onClick={() => onDownloadXLSX(invoice.id, invoice.invoice_number)} title="Original Excel statement for this specific invoice" data-testid="invoice-dialog-download-xlsx">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Download Excel
              </Button>
            )}
            <Button variant="outline" onClick={() => onEmailInvoice(invoice.id, invoice.invoice_number)}>
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            {invoice.status !== 'paid' && (
              <Button
                onClick={() => { onMarkPaid(invoice); onOpenChange(false); }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {invoice.status === 'partially_paid' ? 'Record Payment' : 'Mark Paid'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceDetailsDialog;
